import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { AuthenticationService } from '../authentication/authentication.service';
import { IoService } from '../io/io.service';

// Device authorization for PIN sign-in.
//
// A user with a password session and the `device.authorize` permission can
// authorize the physical device they are on (a warehouse handheld running
// this PWA). The server returns a one-time device key which is kept here, in
// localStorage, scoped to the device like `deviceSettings` — it survives
// sign-out on purpose. While it is valid, the sign-in screen offers a PIN pad
// and a PIN exchanges the device key for a normal (shorter) user session.
//
// The credential expires after a client-configured number of days, is
// revocable by an administrator (Authorized Devices report) or from this
// device, and is revoked by the server after too many bad PINs. Any of those
// surface as a coded rejection which clears the local copy.
export interface DeviceAuth {
  deviceKey: string;
  deviceAuthId: string;
  name: string;
  locationId: string;
  locationName: string;
  authorizedBy: string;
  expiresAt: string; // ISO
  days: number;
}

const STORAGE_KEY = 'deviceAuth';

// Server rejection codes that mean the device authorization is gone.
const TERMINAL_CODES = ['deviceRevoked', 'deviceExpired', 'deviceDisabled'];

const IDLE_CHECK_MS = 30 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

@Injectable({
  providedIn: 'root'
})
export class DeviceAuthService {

  private deviceAuth: DeviceAuth | null = null;
  private deviceAuthChanged = new Subject<DeviceAuth | null>();

  private lastActivityMs = Date.now();
  private idleTimer: any = null;
  private activityListener = () => { this.lastActivityMs = Date.now(); };
  private visibilityListener = () => {
    if (document.visibilityState === 'visible') {
      this.checkIdle();
    }
  };

  constructor(
    private authenticationService: AuthenticationService,
    private ioService: IoService,
    private zone: NgZone,
  ) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      this.deviceAuth = stored ? JSON.parse(stored) : null;
    } catch (e) {
      this.deviceAuth = null;
    }

    // Idle lock follows the session: armed for device-PIN sessions only.
    this.authenticationService.authenticationStateChangedObservable().subscribe(authenticatedId => {
      if (authenticatedId && this.authenticationService.isDeviceSession()) {
        this.startIdleMonitor();
      } else {
        this.stopIdleMonitor();
      }
    });
  }

  deviceAuthChangedObservable(): Observable<DeviceAuth | null> {
    return this.deviceAuthChanged.asObservable();
  }

  getDeviceAuth(): DeviceAuth | null {
    return this.deviceAuth;
  }

  // True when this device holds an authorization that has not yet expired
  // (by the local clock; the server is the final judge on every PIN).
  isAuthorized(): boolean {
    if (!this.deviceAuth || !this.deviceAuth.deviceKey) {
      return false;
    }
    const expires = Date.parse(this.deviceAuth.expiresAt);
    if (!isNaN(expires) && expires <= Date.now()) {
      this.clear();
      return false;
    }
    return true;
  }

  daysRemaining(): number {
    if (!this.deviceAuth) {
      return 0;
    }
    const expires = Date.parse(this.deviceAuth.expiresAt);
    if (isNaN(expires)) {
      return 0;
    }
    return Math.max(0, Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000)));
  }

  // Authorize THIS device. Requires a password session on the server side;
  // the caller is expected to end that session afterwards so the manager's
  // credentials never linger on a shared handheld.
  async authorizeDevice(name: string, locationId: string, days: number): Promise<DeviceAuth> {
    const response: any = await this.ioService.post('/authentication/authorizeDevice', {
      name,
      locationId,
      days,
      userAgent: navigator.userAgent,
    });

    const deviceAuth: DeviceAuth = {
      deviceKey: response.deviceKey,
      deviceAuthId: response.deviceAuthId,
      name: response.name,
      locationId: response.locationId,
      locationName: response.locationName,
      authorizedBy: response.authorizedBy,
      expiresAt: new Date(response.expiresAt).toISOString(),
      days: response.days,
    };

    this.deviceAuth = deviceAuth;
    this.persist();
    return deviceAuth;
  }

  // Remove this device's authorization. Possession of the device key is the
  // proof, so this works from any session type; the local copy is cleared
  // even if the server has already forgotten the device.
  async revokeThisDevice(): Promise<void> {
    const deviceKey = this.deviceAuth && this.deviceAuth.deviceKey;
    try {
      if (deviceKey) {
        await this.ioService.post('/authentication/revokeDevice', { deviceKey });
      }
    } finally {
      this.clear();
    }
  }

  // Unlock with a PIN. Resolves with the server response on success. On a
  // terminal rejection (revoked, expired, disabled) the local authorization
  // is cleared before the error is re-thrown so the caller can fall back to
  // the password form.
  async pinIn(pin: string): Promise<any> {
    if (!this.isAuthorized()) {
      throw { error: { msg: 'This device is not authorized for PIN sign-in.', code: 'deviceRevoked' } };
    }
    try {
      return await this.authenticationService.devicePinIn(this.deviceAuth.deviceKey, pin);
    } catch (err) {
      const code = err && err.error && err.error.code;
      if (TERMINAL_CODES.includes(code)) {
        this.clear();
      }
      throw err;
    }
  }

  clear() {
    this.deviceAuth = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    this.deviceAuthChanged.next(null);
  }

  private persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.deviceAuth));
    } catch (e) {
      console.error('Failed to persist device authorization:', e);
    }
    this.deviceAuthChanged.next(this.deviceAuth);
  }

  // --- Idle lock ------------------------------------------------------------
  //
  // A device-PIN session locks after the client-configured idle period.
  // Keyboard-wedge scans arrive as keydown events, so scanning counts as
  // activity. Waking the screen (visibilitychange) re-checks immediately so
  // a handheld that slept through the threshold locks on wake rather than
  // at the next 30-second tick.

  private startIdleMonitor() {
    this.stopIdleMonitor();
    if (this.authenticationService.getIdleLockMinutes() <= 0) {
      return;
    }
    this.lastActivityMs = Date.now();
    this.zone.runOutsideAngular(() => {
      for (const eventName of ACTIVITY_EVENTS) {
        document.addEventListener(eventName, this.activityListener, { capture: true, passive: true });
      }
      document.addEventListener('visibilitychange', this.visibilityListener);
      this.idleTimer = setInterval(() => this.checkIdle(), IDLE_CHECK_MS);
    });
  }

  private stopIdleMonitor() {
    for (const eventName of ACTIVITY_EVENTS) {
      document.removeEventListener(eventName, this.activityListener, { capture: true } as any);
    }
    document.removeEventListener('visibilitychange', this.visibilityListener);
    if (this.idleTimer) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private checkIdle() {
    const minutes = this.authenticationService.getIdleLockMinutes();
    if (minutes <= 0 || !this.authenticationService.isDeviceSession()) {
      return;
    }
    if (Date.now() - this.lastActivityMs >= minutes * 60 * 1000) {
      this.zone.run(() => this.authenticationService.lock('idle'));
    }
  }
}
